import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { requestOtp, verifyOtp, requestTenantRedirect, changeEmail, setLoginAlert } from '../../actions/appUserAuth';
import { formatMessage } from '../../i18n/language';
import Icon from './Icon';
import LoginAlert from './LoginAlert';
import { ParrotfyLogo } from '../../components';
import './styles.scss';

// Short alias for the react-intl wrapper (see i18n/language) - keeps the JSX
// below readable. All user-facing copy on this screen goes through it.
const tr = (id, values) => formatMessage({ id, values });

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Categorical palette for tenant avatars, drawn from the brand family
// (coral / blue-secondary / green / gradient-mid / blue - see variables.scss).
const AVATAR_COLORS = ['#ff7676', '#7384c7', '#2dd25b', '#b3658c', '#2743b2'];

// In development the Core's OtpAuthenticatable bypass code is a single digit
// ('1'), same as the mobile app - so expect one box instead of six. In
// production the real 6-digit code is required.
const OTP_LENGTH = process.env.NODE_ENV === 'development' ? 1 : 6;
const emptyOtp = () => Array(OTP_LENGTH).fill('');

// Show the tenant search box once the list is long enough to scroll inside the
// fixed-height card.
const SEARCH_MIN_TENANTS = 6;

// A monolith auth failure (inactive account, expired session, …) redirects to
// this SPA with a ?reason= code (see SessionsController + UnifiedFailureApp).
// The code — not a raw message — travels in the URL and maps here to a
// friendly, localized banner.
const REASON_ALERTS = {
  inactive: { id: 'login.reason.inactive', kind: 'error' },
  timeout: { id: 'login.reason.timeout', kind: 'info' },
  unauthenticated: { id: 'login.reason.unauthenticated', kind: 'info' },
  locked: { id: 'login.reason.locked', kind: 'error' },
  unconfirmed: { id: 'login.reason.unconfirmed', kind: 'error' }
};

const isValidEmail = value => EMAIL_RE.test((value || '').trim());

const avatarColor = subdomain => {
  const key = subdomain || '';
  let sum = 0;
  for (let i = 0; i < key.length; i += 1) sum += key.charCodeAt(i);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};

const tenantStatus = tenant => {
  if (tenant.suspended) return 'suspended';
  if (tenant.pending_payment_days) return 'moroso';
  return 'active';
};

const otpErrorMessage = reason => {
  if (reason === 'expired') return tr('login.otp.error.expired');
  if (reason === 'no_tenants') return tr('login.otp.error.noTenants');
  // 'not_found' only reaches the client in development (the Core collapses it
  // into a generic error in production, see CodeLoginsController).
  if (reason === 'not_found') return tr('login.otp.error.notFound');
  if (reason) return tr('login.otp.error.invalid');
  return null;
};

const BrandPanel = () => (
  <div className="p-login__brand">
    <ParrotfyLogo variant="white" className="p-login__brand-logo" />
    <div>
      <div className="p-login__brand-title">
        {tr('login.brand.titleLine1')}
        <br />
        {tr('login.brand.titleLine2')}
      </div>
      <div className="p-login__brand-text">{tr('login.brand.text')}</div>
    </div>
    <div className="p-login__brand-foot">
      <Icon name="shield-check" size={16} />
      <span>{tr('login.brand.foot')}</span>
    </div>
  </div>
);

const focusFirstOtp = () => {
  setTimeout(() => {
    const el = document.querySelector('[data-otp-index="0"]');
    if (el) el.focus();
  }, 60);
};

const UnifiedLogin = () => {
  const dispatch = useDispatch();
  const {
    email: storedEmail,
    otpRequested,
    otpVerified,
    otpError,
    tenants,
    support,
    alert,
    redirecting,
    ongoingRequest
  } = useSelector(state => state.appUserAuth);

  const [email, setEmail] = useState(storedEmail || '');
  const [emailTouched, setEmailTouched] = useState(false);
  const [otp, setOtp] = useState(emptyOtp());
  const [resendIn, setResendIn] = useState(30);
  const [selected, setSelected] = useState(null);
  const [tenantFilter, setTenantFilter] = useState('');
  const [supportSubdomain, setSupportSubdomain] = useState('');
  const timerRef = useRef(null);

  let step = 'email';
  if (otpVerified) step = 'tenant';
  else if (otpRequested) step = 'otp';

  // A lone usable company auto-redirects (see the appUserAuth saga). Show the
  // "entering…" loader for that case — and while tenants are still loading or
  // the redirect is in flight — instead of flashing the one-row picker. An
  // alert means something failed (e.g. the redirect), so fall back to the list
  // where the error banner and a manual retry are reachable.
  //
  // `redirecting` is what keeps the overlay up for the whole handoff: the saga
  // sets window.location and the browser takes a beat to swap documents, so
  // anything keyed only on the in-flight request drops the overlay early and
  // the picker pops back into view mid-navigation.
  // A lone company auto-redirects regardless of suspension (the in-tenant
  // service-suspended page is the gate) — mirrors the saga's auto-select.
  const autoEntering = !support && tenants.length === 1;
  const showEnteringLoader =
    step === 'tenant' &&
    !alert &&
    (redirecting ||
      ongoingRequest.requestTenantRedirect ||
      (!support && (ongoingRequest.fetchTenants || autoEntering)));

  const startCountdown = () => {
    clearInterval(timerRef.current);
    setResendIn(30);
    timerRef.current = setInterval(() => {
      setResendIn(r => (r > 0 ? r - 1 : 0));
    }, 1000);
  };

  // On first load, surface any bounce reason the monolith forwarded as ?reason=
  // (it can't reach this SPA via Rails flash). Then strip the param so a refresh
  // doesn't resurface it.
  useEffect(() => {
    const reason = new URLSearchParams(window.location.search).get('reason');
    const mapped = reason && REASON_ALERTS[reason];
    if (mapped) {
      dispatch(setLoginAlert({ kind: mapped.kind, message: tr(mapped.id) }));
      window.history.replaceState(null, '', window.location.pathname + window.location.hash);
    }
  }, [dispatch]);

  // Start the resend countdown (and focus the first box) whenever we enter the
  // OTP step; stop it when we leave.
  useEffect(() => {
    if (otpRequested && !otpVerified) {
      startCountdown();
      focusFirstOtp();
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [otpRequested, otpVerified]);

  const submitEmail = e => {
    if (e) e.preventDefault();
    if (!isValidEmail(email)) {
      setEmailTouched(true);
      return;
    }
    dispatch(requestOtp(email.trim()));
  };

  const backToEmail = () => {
    clearInterval(timerRef.current);
    setOtp(emptyOtp());
    setSelected(null);
    setTenantFilter('');
    setSupportSubdomain('');
    dispatch(changeEmail());
  };

  const submitSupportSubdomain = e => {
    if (e) e.preventDefault();
    const sub = supportSubdomain.trim().toLowerCase();
    if (sub) dispatch(requestTenantRedirect(sub));
  };

  const tryVerify = next => {
    if (next.every(d => d !== '') && !ongoingRequest.verifyOtp) {
      dispatch(verifyOtp(storedEmail, next.join('')));
    }
  };

  const onOtpDigit = (i, e) => {
    const raw = (e.target.value || '').replace(/\D/g, '');
    const next = otp.slice();

    if (raw.length > 1) {
      const chars = raw.slice(0, OTP_LENGTH).split('');
      for (let k = 0; k < OTP_LENGTH; k += 1) next[k] = chars[k] || '';
      setOtp(next);
      const boxes = e.target.parentElement.children;
      const last = boxes[Math.min(chars.length, OTP_LENGTH) - 1];
      if (last) last.focus();
      tryVerify(next);
      return;
    }

    next[i] = raw.slice(-1);
    setOtp(next);
    if (raw && i < OTP_LENGTH - 1) {
      const box = e.target.parentElement.children[i + 1];
      if (box) box.focus();
    }
    tryVerify(next);
  };

  const onOtpKey = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) {
      const prev = e.target.parentElement.children[i - 1];
      if (prev) prev.focus();
    }
  };

  const resend = () => {
    setOtp(emptyOtp());
    dispatch(requestOtp(storedEmail));
    startCountdown();
    focusFirstOtp();
  };

  // Payment-suspended tenants are enterable: the service-suspended page inside
  // the target tenant is the gate, not this picker. Every listed tenant is
  // selectable.
  const selectTenant = tenant => {
    setSelected(tenant.subdomain);
    dispatch(requestTenantRedirect(tenant.subdomain));
  };

  const enteringTenant = () => {
    if (selected) return tenants.find(t => t.subdomain === selected);
    return tenants.length === 1 ? tenants[0] : null;
  };

  const renderEmail = () => {
    const showError = emailTouched && !isValidEmail(email);
    return (
      <div className="p-login__screen p-login__screen--narrow">
        <div className="p-login__eyebrow">{tr('login.email.eyebrow')}</div>
        <h1 className="p-login__title">{tr('login.email.title')}</h1>
        <p className="p-login__lead">{tr('login.email.lead')}</p>

        <form onSubmit={submitEmail}>
          {/* eslint-disable-next-line jsx-a11y/label-has-for */}
          <label className="p-login__label" htmlFor="p-login-email">
            {tr('login.email.label')}
            <div className="p-login__input-wrap">
              <span className="p-login__input-icon">
                <Icon name="mail" size={18} />
              </span>
              <input
                id="p-login-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder={tr('login.email.placeholder')}
                className={`p-login__input${showError ? ' p-login__input--error' : ''}`}
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  setEmailTouched(false);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') submitEmail(e);
                }}
              />
            </div>
          </label>
          {showError && (
            <div className="p-login__field-error">
              <Icon name="alert-circle" size={14} />
              <span>{tr('login.email.invalid')}</span>
            </div>
          )}

          <button type="submit" className="p-login__btn" disabled={ongoingRequest.requestOtp}>
            <span>{tr('login.email.submit')}</span>
            <Icon name="arrow-right" size={16} />
          </button>
        </form>

        <p className="p-login__legal">
          {tr('login.legal', {
            privacy: chunks => (
              <a href="https://www.parrotfy.com/es/privacy" target="_blank" rel="noopener noreferrer">
                {chunks}
              </a>
            ),
            terms: chunks => (
              <a href="https://www.parrotfy.com/es/terms-of-service" target="_blank" rel="noopener noreferrer">
                {chunks}
              </a>
            )
          })}
        </p>

        <p className="p-login__aside">
          {tr('login.email.noAccount', { signup: chunks => <a href="/onboarding">{chunks}</a> })}
        </p>
      </div>
    );
  };

  const renderOtp = () => {
    const expired = otpError === 'expired';
    const mm = Math.floor(resendIn / 60);
    const ss = String(resendIn % 60).padStart(2, '0');
    const message = otpErrorMessage(otpError);

    return (
      <div className="p-login__screen p-login__screen--narrow">
        <button type="button" className="p-login__back" onClick={backToEmail}>
          <Icon name="arrow-left" size={15} />
          <span>{tr('login.otp.back')}</span>
        </button>

        <h1 className="p-login__title">{tr('login.otp.title')}</h1>
        <p className="p-login__lead">
          {tr('login.otp.lead')} <strong>{storedEmail}</strong>
        </p>

        <div className={`p-login__otp-boxes${OTP_LENGTH === 1 ? ' p-login__otp-boxes--single' : ''}`}>
          {otp.map((val, i) => (
            <input
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              data-otp-index={i}
              inputMode="numeric"
              maxLength={6}
              className="p-login__otp-box"
              value={val}
              onChange={e => onOtpDigit(i, e)}
              onKeyDown={e => onOtpKey(i, e)}
            />
          ))}
        </div>

        {message && (
          <div className={`p-login__otp-error${expired ? ' p-login__otp-error--expired' : ''}`}>
            <Icon name={expired ? 'clock' : 'alert-circle'} size={16} />
            <span>{message}</span>
          </div>
        )}

        <div className="p-login__resend">
          <span>{tr('login.otp.resendQuestion')}</span>
          {resendIn <= 0 ? (
            <button type="button" className="p-login__resend-btn" onClick={resend}>
              <Icon name="rotate-cw" size={14} />
              <span>{tr('login.otp.resend')}</span>
            </button>
          ) : (
            <span className="p-login__resend-wait">{tr('login.otp.resendWait', { time: `${mm}:${ss}` })}</span>
          )}
        </div>
      </div>
    );
  };

  const renderTenantRow = tenant => {
    const status = tenantStatus(tenant);
    const name = tenant.company_name || tenant.subdomain;
    const initial = (name[0] || '?').toUpperCase();

    let badgeText;
    let badgeTitle;
    // Every row is clickable now, so all use the chevron affordance; the badge
    // still carries the status.
    const trailIcon = 'chevron-right';
    if (status === 'suspended') {
      badgeText = tr('login.status.suspended');
    } else if (status === 'moroso') {
      badgeText = tr('login.status.pending');
      badgeTitle = tr('login.tenant.overdueDays', { days: tenant.pending_payment_days });
    } else {
      badgeText = tr('login.status.active');
    }

    return (
      <button
        key={tenant.subdomain}
        type="button"
        className={`p-login__row${status !== 'active' ? ` p-login__row--${status}` : ''}`}
        onClick={() => selectTenant(tenant)}
      >
        <span className="p-login__avatar" style={{ background: avatarColor(tenant.subdomain) }}>
          {initial}
        </span>
        <span className="p-login__row-main">
          <span className="p-login__row-head">
            <span className="p-login__row-name">{name}</span>
            <span className={`p-login__badge p-login__badge--${status}`} title={badgeTitle}>
              {badgeText}
            </span>
          </span>
          <span className="p-login__row-sub">{tenant.subdomain}.parrotfy.com</span>
        </span>
        <Icon name={trailIcon} size={18} />
      </button>
    );
  };

  const renderSupport = () => (
    <div className="p-login__screen p-login__screen--narrow">
      <div className="p-login__emailchip">
        <Icon name="user-round" size={15} />
        <span>{storedEmail}</span>
        <button type="button" onClick={backToEmail}>
          {tr('login.emailchip.change')}
        </button>
      </div>
      <h1 className="p-login__title">{tr('login.support.title')}</h1>
      <p className="p-login__lead">{tr('login.support.lead')}</p>

      <form onSubmit={submitSupportSubdomain}>
        {/* eslint-disable-next-line jsx-a11y/label-has-for */}
        <label className="p-login__label" htmlFor="p-login-subdomain">
          {tr('login.support.label')}
          <div className="p-login__input-wrap">
            <span className="p-login__input-icon">
              <Icon name="globe" size={18} />
            </span>
            <input
              id="p-login-subdomain"
              type="text"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck="false"
              placeholder={tr('login.support.placeholder')}
              className="p-login__input"
              value={supportSubdomain}
              onChange={e => setSupportSubdomain(e.target.value)}
            />
          </div>
        </label>
        <p className="p-login__hint">
          {(supportSubdomain.trim() || tr('login.support.placeholder')).toLowerCase()}.parrotfy.com
        </p>

        <button
          type="submit"
          className="p-login__btn"
          disabled={!supportSubdomain.trim() || ongoingRequest.requestTenantRedirect}
        >
          <span>{tr('login.support.submit')}</span>
          <Icon name="arrow-right" size={16} />
        </button>
      </form>
    </div>
  );

  const renderTenant = () => {
    if (support) return renderSupport();

    const showSearch = tenants.length > SEARCH_MIN_TENANTS;
    const q = tenantFilter.trim().toLowerCase();
    const filtered = q
      ? tenants.filter(t => {
          const name = (t.company_name || t.subdomain || '').toLowerCase();
          return name.includes(q) || (t.subdomain || '').toLowerCase().includes(q);
        })
      : tenants;

    return (
      <div className="p-login__screen p-login__screen--wide">
        <div className="p-login__emailchip">
          <Icon name="user-round" size={15} />
          <span>{storedEmail}</span>
          <button type="button" onClick={backToEmail}>
            {tr('login.emailchip.change')}
          </button>
        </div>
        <h1 className="p-login__title">{tr('login.tenant.title')}</h1>

        {showSearch && (
          <div className="p-login__search-wrap">
            <span className="p-login__search-icon">
              <Icon name="search" size={16} />
            </span>
            <input
              type="text"
              className="p-login__search"
              placeholder={tr('login.tenant.search')}
              value={tenantFilter}
              onChange={e => setTenantFilter(e.target.value)}
            />
          </div>
        )}

        <div className="p-login__list">
          {filtered.length ? (
            filtered.map(renderTenantRow)
          ) : (
            <p className="p-login__empty">{tr('login.tenant.empty', { query: tenantFilter })}</p>
          )}
        </div>

        <p className="p-login__foot-info">
          <Icon name="info" size={14} />
          <span>{tr('login.tenant.footInfo')}</span>
        </p>
      </div>
    );
  };

  const renderOverlay = () => {
    const entering = enteringTenant();
    // Support flow has no tenant object - fall back to the typed subdomain.
    const sub = entering ? entering.subdomain : supportSubdomain.trim().toLowerCase();
    const name = entering ? entering.company_name || entering.subdomain : sub;
    return (
      <div className="p-login__overlay">
        <div className="p-login__spinner" />
        <div>
          <div className="p-login__overlay-title">
            {name ? tr('login.overlay.entering', { name }) : tr('login.overlay.loading')}
          </div>
          {sub && <div className="p-login__overlay-sub">{sub}.parrotfy.com</div>}
        </div>
        <button type="button" className="p-login__overlay-cancel" onClick={backToEmail}>
          {tr('login.overlay.cancel')}
        </button>
      </div>
    );
  };

  return (
    <div className="p-login">
      <div className="p-login__card">
        <BrandPanel />
        <div className="p-login__body">
          <LoginAlert />
          {step === 'email' && renderEmail()}
          {step === 'otp' && renderOtp()}
          {step === 'tenant' && !showEnteringLoader && renderTenant()}
        </div>
        {showEnteringLoader && renderOverlay()}
      </div>
    </div>
  );
};

export default UnifiedLogin;
